import React, { useEffect, useState } from 'react';
import APIService from '../services/api';
import { useConfirm } from './ConfirmDialog';

/**
 * Cartes profil et photos d'un dossier AO — en lot (04.11).
 *
 * Trois gestes, pour le dossier entier ou pour un lot (`besoinId`) :
 *  1. « Générer les cartes profil » → POST /ao/:id/cartes-profil
 *     { besoin_id?, photo?, regenerer? } — une tâche de fond (`ao_cartes_profil`),
 *     suivie par le mécanisme de l'écran (onJob) ; les lignes « Carte profil »
 *     se mettent à jour au rescan qui suit la fin de la tâche.
 *  2. « Relire les cartes (beta) » → POST /ao/:id/cartes-profil/relecture
 *     { besoin_id?, forcer? } — Leman relit, ne modifie rien. Un lot répond
 *     tout de suite (200), un dossier lance une tâche (202). Le dernier
 *     résultat se relit sans dépense : GET …/relecture?besoin_id=.
 *  3. « Photos LinkedIn » → GET /ao/:id/photos (état), POST /ao/:id/photos
 *     (demande) : le moteur de sourcing passe sur les profils publics, en
 *     lecture seule, sans contacter personne.
 *
 * Le serveur décide et dit : ses messages sont affichés tels quels. Une route
 * absente (404/501 sans message) le dit — « arrive côté serveur » — sans rien
 * écrire.
 */
const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} aria-hidden="true" />;
const ARRIVE = 'arrive côté serveur — rien n\'a été lancé.';

const GRAVITE = {
  bloquant: { label: 'Bloquant', bg: 'var(--erp-danger-soft)', fg: 'var(--erp-danger)' },
  a_corriger: { label: 'À corriger', bg: 'var(--erp-warning-soft)', fg: 'var(--erp-warning)' },
  info: { label: 'Info', bg: 'var(--erp-surface-muted)', fg: 'var(--erp-text-secondary)' },
};
const ETAT = {
  ok: { label: 'Conforme', bg: 'var(--erp-success-soft)', fg: 'var(--erp-success)' },
  conforme: { label: 'Conforme', bg: 'var(--erp-success-soft)', fg: 'var(--erp-success)' },
  a_corriger: GRAVITE.a_corriger,
  bloquant: GRAVITE.bloquant,
  bloquante: GRAVITE.bloquant,
  echec: { label: 'Relecture en échec', bg: 'var(--erp-danger-soft)', fg: 'var(--erp-danger)' },
  non_relue: { label: 'Non relue', bg: 'var(--erp-surface-muted)', fg: 'var(--erp-text-secondary)' },
};
const RESULTAT_PHOTO = {
  posee: 'Photo posée', deja_photo: 'Avait déjà une photo', sans_photo: 'Pas de photo sur le profil',
  introuvable: 'Profil introuvable', refusee: 'Photo refusée', en_attente: 'En attente', sans_reponse: 'Sans réponse',
};
const COMPTEURS_PHOTO = [
  ['demandees', 'demandées'], ['posees', 'posées'], ['deja_photo', 'déjà une photo'], ['sans_photo', 'sans photo'],
  ['introuvables', 'introuvables'], ['refusees', 'refusées'], ['en_attente', 'en attente'], ['sans_reponse', 'sans réponse'],
];
const dateHeure = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime())
    ? `${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
    : null;
};

const Pastille = ({ def, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flex: 'none', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', background: def.bg, color: def.fg }}>{children || def.label}</span>
);

// Une fenêtre aux classes ERP : en-tête, corps, actions.
function Fenetre({ titre, sousTitre, icone, large, onClose, actions, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="erp-dialog-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="erp-dialog" role="dialog" aria-modal="true" aria-label={titre}
        style={large ? { width: 'min(100%, 780px)', maxHeight: '88vh', display: 'flex', flexDirection: 'column' } : undefined}>
        <header className="erp-dialog__header">
          <span className="erp-dialog__icon"><BI icon={icone} /></span>
          <div className="erp-dialog__heading">
            <h2>{titre}</h2>
            {sousTitre && <p>{sousTitre}</p>}
          </div>
          <button type="button" className="erp-dialog__close" onClick={onClose} aria-label="Fermer"><BI icon="x-lg" /></button>
        </header>
        <div className="erp-dialog__body" style={large ? { overflowY: 'auto', paddingBottom: '16px' } : undefined}>{children}</div>
        {actions && <footer className="erp-dialog__actions">{actions}</footer>}
      </section>
    </div>
  );
}

const lireJson = async (r) => r.json().catch(() => ({}));
// Le message du serveur fait foi ; sans message, une route absente le dit.
const raison = (r, j, quoi) => j.message || j.error
  || ((r.status === 404 || r.status === 501) ? `${quoi} ${ARRIVE}` : `${quoi} : erreur ${r.status}.`);

export default function AoCartesPhotos({ aoId, besoinId = null, lotLabel = null, compact = false, showNotification, onJob, onFini, jobsFinis = 0 }) {
  const { confirm, confirmElement } = useConfirm();
  const authHeaders = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
  const jsonHeaders = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });
  const portee = besoinId != null ? (lotLabel ? `le lot ${lotLabel}` : 'ce lot') : 'tout le dossier';
  const qs = besoinId != null ? `?besoin_id=${encodeURIComponent(besoinId)}` : '';
  const corpsPortee = besoinId != null ? { besoin_id: Number(besoinId) } : {};
  const notifier = (msg, ton) => showNotification?.(msg, ton);

  // ── 1. Génération des cartes ──────────────────────────────────────────────
  const [genOuverte, setGenOuverte] = useState(false);
  const [avecPhoto, setAvecPhoto] = useState(false);
  const [regenerer, setRegenerer] = useState(false);
  const [genEnCours, setGenEnCours] = useState(false);
  const [bilanGen, setBilanGen] = useState(null); // { message, sans_cv, ecartes }

  const lancerCartes = async () => {
    setGenEnCours(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/cartes-profil`, {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ ...corpsPortee, photo: avecPhoto, regenerer }),
      });
      const j = await lireJson(r);
      if (!r.ok || j.success === false) {
        notifier(raison(r, j, 'La génération des cartes'), r.status === 422 ? 'info' : 'error');
        if (Array.isArray(j.sans_cv) || Array.isArray(j.ecartes)) setBilanGen({ message: j.message || j.error, sans_cv: j.sans_cv || [], ecartes: j.ecartes || [] });
        return;
      }
      setGenOuverte(false);
      if (j.message) notifier(j.message, 'success');
      if ((j.sans_cv || []).length || (j.ecartes || []).length) setBilanGen({ message: j.message, sans_cv: j.sans_cv || [], ecartes: j.ecartes || [] });
      if (j.job_id) onJob?.({ job_id: j.job_id, job: { type: 'ao_cartes_profil', label: 'Préparation des cartes…', total: j.cartes ?? undefined } });
      else onFini?.();
    } catch (e) { notifier(`Génération des cartes impossible : ${e.message}`, 'error'); }
    finally { setGenEnCours(false); }
  };

  // ── 2. Relecture des cartes (beta) ────────────────────────────────────────
  const [relOuverte, setRelOuverte] = useState(false);
  const [relectures, setRelectures] = useState(null); // lignes à afficher
  const [relBilan, setRelBilan] = useState(null);
  const [relChargement, setRelChargement] = useState(false);
  const [relEnCours, setRelEnCours] = useState(false);
  const [relRoute, setRelRoute] = useState(null); // message quand la route manque

  const chargerRelecture = async () => {
    setRelChargement(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/cartes-profil/relecture${qs}`, { headers: authHeaders() });
      const j = await lireJson(r);
      if (!r.ok || j.success === false) { setRelRoute(raison(r, j, 'La relecture des cartes')); setRelectures([]); return; }
      setRelRoute(null);
      setRelectures(Array.isArray(j.relectures) ? j.relectures : []);
    } catch (e) { setRelRoute(`Relecture illisible : ${e.message}`); setRelectures([]); }
    finally { setRelChargement(false); }
  };
  const ouvrirRelecture = () => { setRelOuverte(true); setRelBilan(null); chargerRelecture(); };

  // Une tâche de l'écran vient de finir : la relecture affichée se relit.
  useEffect(() => {
    if (relOuverte && jobsFinis > 0) chargerRelecture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsFinis]);

  const lancerRelecture = async (forcer) => {
    const ok = await confirm({
      title: 'Relire les cartes (beta)',
      subtitle: portee,
      message: `Leman relit les cartes profil de ${portee} et signale ce qui ne tient pas face au CV.`,
      detail: `La relecture ne modifie aucune carte. C'est une dépense d'IA${forcer ? ' — les cartes déjà relues et inchangées sont relues à nouveau' : ' ; une carte déjà relue et inchangée n\'est pas relue'}.`,
      confirmLabel: 'Lancer la relecture', tone: 'primary', icon: 'card-checklist',
    });
    if (!ok) return;
    setRelEnCours(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/cartes-profil/relecture`, {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ ...corpsPortee, ...(forcer ? { forcer: true } : {}) }),
      });
      const j = await lireJson(r);
      if (!r.ok || j.success === false) { notifier(raison(r, j, 'La relecture des cartes'), r.status === 422 ? 'info' : 'error'); return; }
      if (j.job_id) {
        onJob?.({ job_id: j.job_id, job: { type: 'ao_cartes_profil_relecture', label: 'Préparation de la relecture…' } });
        notifier(j.message || 'Relecture lancée — le panneau se met à jour à la fin.', 'success');
        return;
      }
      if (Array.isArray(j.resultats)) setRelectures(j.resultats.map(x => ({ ...x, relue: true, a_jour: true })));
      setRelBilan(j.bilan || null);
      if (j.message) notifier(j.message, 'success');
    } catch (e) { notifier(`Relecture impossible : ${e.message}`, 'error'); }
    finally { setRelEnCours(false); }
  };

  // ── 3. Photos LinkedIn ────────────────────────────────────────────────────
  const [phOuverte, setPhOuverte] = useState(false);
  const [phEtat, setPhEtat] = useState(null);
  const [phChargement, setPhChargement] = useState(false);
  const [phRoute, setPhRoute] = useState(null);
  const [phEnCours, setPhEnCours] = useState(false);
  const [phRetour, setPhRetour] = useState(null); // { ton, message }

  const chargerPhotos = async () => {
    setPhChargement(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/photos${qs}`, { headers: authHeaders() });
      const j = await lireJson(r);
      if (!r.ok || j.success === false) { setPhRoute(raison(r, j, 'Les photos LinkedIn')); setPhEtat(null); return; }
      setPhRoute(null);
      setPhEtat(j);
    } catch (e) { setPhRoute(`État des photos illisible : ${e.message}`); }
    finally { setPhChargement(false); }
  };
  const ouvrirPhotos = () => { setPhOuverte(true); setPhRetour(null); chargerPhotos(); };

  const demanderPhotos = async () => {
    const n = phEtat?.eligibles?.a_demander;
    const ok = await confirm({
      title: 'Demander les photos LinkedIn',
      subtitle: portee,
      message: `Le moteur de sourcing passe sur les profils publics${n != null ? ` de ${n} candidat${n > 1 ? 's' : ''}` : ''} pour en reprendre la photo.`,
      detail: 'Lecture seule : personne n\'est contacté, aucun message n\'est envoyé. Une fiche qui a déjà une photo n\'est pas touchée.',
      confirmLabel: 'Demander les photos', tone: 'primary', icon: 'person-bounding-box',
    });
    if (!ok) return;
    setPhEnCours(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/photos`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(corpsPortee) });
      const j = await lireJson(r);
      if (!r.ok || j.success === false) {
        // bac_a_sable, agent_absent, deja_demande, moteur_ancien (501),
        // moteur_injoignable, moteur_ko : le texte du serveur, tel quel.
        const msg = raison(r, j, 'La demande de photos');
        setPhRetour({ ton: 'error', message: msg });
        notifier(msg, j.code === 'deja_demande' || j.code === 'moteur_ancien' ? 'info' : 'error');
      } else {
        const msg = j.message || `${j.demandees ?? 0} photo(s) demandée(s).`;
        setPhRetour({ ton: 'success', message: msg });
        notifier(msg, 'success');
      }
      chargerPhotos();
    } catch (e) { notifier(`Demande de photos impossible : ${e.message}`, 'error'); }
    finally { setPhEnCours(false); }
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  const btn = `erp-btn erp-btn--secondary${compact ? ' erp-btn--sm' : ''}`;
  const spin = { animation: 'ao-spin .8s linear infinite' };
  const texteFaible = { fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.5 };
  const titreBloc = { fontSize: '11px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-3)', margin: '14px 0 6px' };

  const listeNoms = (liste, titre) => (liste && liste.length > 0 ? (
    <div>
      <div style={titreBloc}>{titre} ({liste.length})</div>
      <ul style={{ margin: 0, paddingLeft: '18px', ...texteFaible }}>
        {liste.map((x, i) => (
          <li key={`${x.besoin_id}-${x.candidate_id}-${i}`}>
            <b style={{ color: 'var(--text-strong)' }}>{x.nom || `Candidat ${x.candidate_id}`}</b>
            {x.lot ? <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px' }}> · {x.lot}</span> : null}
            {x.raison ? ` — ${x.raison}` : ''}
          </li>
        ))}
      </ul>
    </div>
  ) : null);

  // Relectures regroupées par lot, dans l'ordre servi.
  const parLot = [];
  for (const x of relectures || []) {
    const cle = x.lot || String(x.besoin_id ?? '');
    let g = parLot.find(p => p.cle === cle);
    if (!g) { g = { cle, lot: x.lot, lignes: [] }; parLot.push(g); }
    g.lignes.push(x);
  }

  return (
    <>
      <button type="button" className={btn} onClick={() => { setBilanGen(null); setGenOuverte(true); }} disabled={genEnCours}
        title={`Générer la carte profil de chaque candidat de ${portee} et la déposer dans le dossier partagé`}>
        <BI icon={genEnCours ? 'arrow-repeat' : 'person-vcard'} style={genEnCours ? spin : undefined} /> {compact ? 'Cartes profil' : 'Générer toutes les cartes profil'}
      </button>
      <button type="button" className={btn} onClick={ouvrirRelecture}
        title={`Relecture des cartes de ${portee} par Leman — signale, ne modifie rien`}>
        <BI icon="card-checklist" /> {compact ? 'Relire (beta)' : 'Relire les cartes (beta)'}
      </button>
      <button type="button" className={btn} onClick={ouvrirPhotos}
        title={`Reprendre la photo des profils LinkedIn publics de ${portee}`}>
        <BI icon="person-bounding-box" /> Photos LinkedIn
      </button>

      {genOuverte && (
        <Fenetre titre="Générer les cartes profil" sousTitre={portee} icone="person-vcard" onClose={() => setGenOuverte(false)}
          actions={(<>
            <button type="button" className="erp-btn erp-btn--secondary" onClick={() => setGenOuverte(false)}>Annuler</button>
            <button type="button" className="erp-btn erp-btn--primary" onClick={lancerCartes} disabled={genEnCours}>
              <BI icon={genEnCours ? 'arrow-repeat' : 'check-lg'} style={genEnCours ? spin : undefined} /> Générer les cartes
            </button>
          </>)}>
          <p className="erp-dialog__message">Une carte profil par candidat de {portee}, déposée dans son dossier du dossier partagé.</p>
          <p className="erp-dialog__detail">Leman rédige l'argumentaire d'une carte quand il manque ou qu'il est périmé : c'est une dépense d'IA. Un candidat sans CV format entreprise pour son lot est sauté, et c'est dit.</p>
          <div style={{ display: 'grid', gap: '9px', marginTop: '14px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: 'var(--text)', cursor: 'pointer' }}>
              <input type="checkbox" checked={avecPhoto} onChange={e => setAvecPhoto(e.target.checked)} style={{ marginTop: '3px' }} />
              <span>Avec la photo (accord des candidats)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: 'var(--text)', cursor: 'pointer' }}>
              <input type="checkbox" checked={regenerer} onChange={e => setRegenerer(e.target.checked)} style={{ marginTop: '3px' }} />
              <span>Régénérer les cartes existantes</span>
            </label>
          </div>
          {bilanGen && (
            <div style={{ marginTop: '10px' }}>
              {bilanGen.message && <p style={texteFaible}>{bilanGen.message}</p>}
              {listeNoms(bilanGen.sans_cv, 'Sans CV format entreprise')}
              {listeNoms(bilanGen.ecartes, 'Écartés')}
            </div>
          )}
        </Fenetre>
      )}

      {bilanGen && !genOuverte && (
        <Fenetre titre="Cartes profil — ce qui a été sauté" sousTitre={portee} icone="person-vcard" onClose={() => setBilanGen(null)}
          actions={<button type="button" className="erp-btn erp-btn--primary" onClick={() => setBilanGen(null)}><BI icon="check-lg" /> Compris</button>}>
          {bilanGen.message && <p className="erp-dialog__message">{bilanGen.message}</p>}
          {listeNoms(bilanGen.sans_cv, 'Sans CV format entreprise')}
          {listeNoms(bilanGen.ecartes, 'Écartés')}
        </Fenetre>
      )}

      {relOuverte && (
        <Fenetre large titre="Relecture des cartes (beta)" sousTitre={portee} icone="card-checklist" onClose={() => setRelOuverte(false)}
          actions={(<>
            <button type="button" className="erp-btn erp-btn--secondary" onClick={() => lancerRelecture(true)} disabled={relEnCours}
              title="Relire aussi les cartes déjà relues et inchangées">Tout relire</button>
            <button type="button" className="erp-btn erp-btn--primary" onClick={() => lancerRelecture(false)} disabled={relEnCours}>
              <BI icon={relEnCours ? 'arrow-repeat' : 'card-checklist'} style={relEnCours ? spin : undefined} /> {relEnCours ? 'Relecture…' : 'Lancer la relecture'}
            </button>
          </>)}>
          <p style={texteFaible}>Leman confronte chaque carte au CV du lot et dit ce qui ne tient pas. La relecture ne modifie aucune carte : la correction se fait en régénérant la carte.</p>
          {relBilan && (
            <p style={{ ...texteFaible, fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', marginTop: '8px' }}>
              {relBilan.relues ?? 0} relue(s) · {relBilan.bloquantes ?? 0} bloquante(s) · {relBilan.a_corriger ?? 0} à corriger · {relBilan.ok ?? 0} conforme(s)
              {relBilan.deja_a_jour ? ` · ${relBilan.deja_a_jour} déjà à jour` : ''}{relBilan.echecs ? ` · ${relBilan.echecs} en échec` : ''}
            </p>
          )}
          {relChargement && <p style={texteFaible}><BI icon="arrow-repeat" style={spin} /> Lecture du dernier résultat…</p>}
          {relRoute && !relChargement && <p style={{ ...texteFaible, color: 'var(--erp-danger)' }}>{relRoute}</p>}
          {!relChargement && !relRoute && relectures && relectures.length === 0 && (
            <p style={texteFaible}>Aucune relecture pour {portee}. Lancez-la pour savoir si les cartes tiennent face aux CV.</p>
          )}
          {parLot.map(g => (
            <div key={g.cle}>
              <div style={titreBloc}>{g.lot ? <span style={{ fontFamily: "'IBM Plex Mono',monospace" }}>{g.lot}</span> : 'Lot'}</div>
              <div style={{ display: 'grid', gap: '8px' }}>
                {g.lignes.map(x => {
                  const etat = x.relue === false ? ETAT.non_relue : (ETAT[x.etat] || { label: x.etat || '—', bg: 'var(--erp-surface-muted)', fg: 'var(--erp-text-secondary)' });
                  const constats = Array.isArray(x.constats) ? x.constats : [];
                  return (
                    <div key={`${x.besoin_id}-${x.candidate_id}`} style={{ border: '1px solid var(--border)', borderRadius: '11px', padding: '11px 13px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}>{x.nom || `Candidat ${x.candidate_id}`}</span>
                        <Pastille def={etat} />
                        {x.a_jour === false && <Pastille def={GRAVITE.a_corriger}><BI icon="arrow-clockwise" /> à relire</Pastille>}
                        {x.deja_a_jour && <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>déjà relue, inchangée</span>}
                        {x.relu_le && <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-faint)', fontFamily: "'IBM Plex Mono',monospace" }}>{dateHeure(x.relu_le)}</span>}
                      </div>
                      {x.resume && <p style={{ ...texteFaible, margin: '6px 0 0' }}>{x.resume}</p>}
                      {constats.length > 0 && (
                        <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'grid', gap: '7px' }}>
                          {constats.map((c, i) => (
                            <li key={`${c.code || 'c'}-${i}`} style={{ borderTop: '1px solid var(--hair)', paddingTop: '7px' }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
                                <Pastille def={GRAVITE[c.gravite] || GRAVITE.info} />
                                <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{c.libelle}</span>
                              </div>
                              {c.citation && <div style={{ ...texteFaible, margin: '4px 0 0', paddingLeft: '9px', borderLeft: '2px solid var(--border)', fontStyle: 'italic' }}>« {c.citation} »</div>}
                              {c.detail && <div style={{ ...texteFaible, marginTop: '3px' }}>{c.detail}</div>}
                              {c.quoi_faire && <div style={{ ...texteFaible, marginTop: '3px', color: 'var(--text)' }}><BI icon="arrow-return-right" /> {c.quoi_faire}</div>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </Fenetre>
      )}

      {phOuverte && (
        <Fenetre large titre="Photos LinkedIn" sousTitre={portee} icone="person-bounding-box" onClose={() => setPhOuverte(false)}
          actions={(<>
            <button type="button" className="erp-btn erp-btn--secondary" onClick={chargerPhotos} disabled={phChargement}>
              <BI icon="arrow-clockwise" style={phChargement ? spin : undefined} /> Actualiser
            </button>
            <button type="button" className="erp-btn erp-btn--primary" onClick={demanderPhotos}
              disabled={phEnCours || !!phRoute || (phEtat?.eligibles?.a_demander === 0)}>
              <BI icon={phEnCours ? 'arrow-repeat' : 'person-bounding-box'} style={phEnCours ? spin : undefined} /> Demander les photos
            </button>
          </>)}>
          <p style={texteFaible}>Le moteur de sourcing reprend la photo des profils LinkedIn publics des candidats, en lecture seule, sans contacter personne.</p>
          {phRetour && <p style={{ ...texteFaible, marginTop: '8px', color: phRetour.ton === 'error' ? 'var(--erp-danger)' : 'var(--erp-success)' }}>{phRetour.message}</p>}
          {phChargement && !phEtat && <p style={texteFaible}><BI icon="arrow-repeat" style={spin} /> Lecture de l'état…</p>}
          {phRoute && <p style={{ ...texteFaible, color: 'var(--erp-danger)' }}>{phRoute}</p>}
          {phEtat && (() => {
            const el = phEtat.eligibles || {};
            const d = phEtat.derniere;
            const statut = d ? ({ demandee: { label: 'Demandée', ...GRAVITE.a_corriger }, terminee: { label: 'Terminée', bg: 'var(--erp-success-soft)', fg: 'var(--erp-success)' }, erreur: { label: 'En erreur', bg: 'var(--erp-danger-soft)', fg: 'var(--erp-danger)' } }[d.statut] || { label: d.statut, ...GRAVITE.info }) : null;
            return (<>
              <div style={titreBloc}>Candidats</div>
              <p style={{ ...texteFaible, fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px' }}>
                {el.a_demander ?? 0} à demander · {el.sans_lien ?? 0} sans lien LinkedIn · {el.deja_photo ?? 0} avec photo
              </p>
              <div style={titreBloc}>Dernière demande</div>
              {!d ? <p style={texteFaible}>Aucune demande pour l'instant.</p> : (<>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <Pastille def={statut} />
                  {dateHeure(d.demande_le) && <span style={texteFaible}>demandée le {dateHeure(d.demande_le)}</span>}
                  {dateHeure(d.termine_le) && <span style={texteFaible}>· terminée le {dateHeure(d.termine_le)}</span>}
                </div>
                {d.raison && <p style={{ ...texteFaible, marginTop: '5px' }}>{d.raison}</p>}
                {d.compteurs && (
                  <p style={{ ...texteFaible, fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', marginTop: '5px' }}>
                    {COMPTEURS_PHOTO.filter(([k]) => d.compteurs[k]).map(([k, l]) => `${d.compteurs[k]} ${l}`).join(' · ') || 'aucun résultat encore'}
                  </p>
                )}
                {Array.isArray(d.fiches) && d.fiches.length > 0 && (
                  <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: '4px' }}>
                    {d.fiches.map(f => (
                      <li key={f.candidate_id} style={{ display: 'flex', gap: '8px', alignItems: 'baseline', fontSize: '12.5px', borderTop: '1px solid var(--hair)', paddingTop: '4px' }}>
                        <span style={{ fontWeight: 600, color: 'var(--text-strong)', minWidth: 0 }}>{f.nom || `Candidat ${f.candidate_id}`}</span>
                        <span style={{ color: 'var(--text-2)' }}>{f.libelle || RESULTAT_PHOTO[f.resultat] || f.resultat}</span>
                        {f.raison && <span style={{ color: 'var(--text-faint)' }}>— {f.raison}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </>)}
            </>);
          })()}
        </Fenetre>
      )}
      {confirmElement}
    </>
  );
}
